import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Alert,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Delete,
  Add,
  Person,
  Group,
  CheckCircle,
  HourglassEmpty,
  Email as EmailIcon,
  FolderOpen,
  CreateNewFolder,
  ArrowBack,
  PersonAdd,
  Edit,
  Save,
  Description,
  FolderShared,
} from '@mui/icons-material';
import { praAPI, investigationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const statusColors = {
  draft: 'default',
  pending_review: 'info',
  sent: 'primary',
  acknowledged: 'info',
  fulfilled: 'success',
  partially_fulfilled: 'warning',
  denied: 'error',
  overdue: 'error',
};

const statusLabels = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  fulfilled: 'Fulfilled',
  partially_fulfilled: 'Partial',
  denied: 'Denied',
  overdue: 'Overdue',
};

const QuickNewRequestCard = ({ destinations, investigationId, onCreated }) => {
  const navigate = useNavigate();
  const [destinationId, setDestinationId] = useState('');
  const [aspect, setAspect] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!destinationId || !aspect.trim()) {
      setError('Select an agency and describe the aspect you want records about.');
      return;
    }
    setSubmitting(true);
    try {
      const dest = destinations.find((d) => d.id === destinationId);
      const subject = `Public Records Request: ${aspect.substring(0, 80)}`;
      const resp = await praAPI.createRequest({
        destination_id: destinationId,
        natural_language_input: aspect.trim(),
        subject,
        // When inside a folder, attach the new request to it at creation time.
        ...(investigationId ? { investigation_id: investigationId } : {}),
      });
      setAspect('');
      if (onCreated) onCreated(resp.data);
      navigate(`/requests/new?draft_id=${resp.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to create draft');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0ddd6',
        borderRadius: 2,
        p: 2.5,
        mb: 3,
        background: 'linear-gradient(180deg, #ffffff 0%, #fafaf5 100%)',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          fontSize: '1rem',
          color: '#00075e',
          mb: 0.5,
        }}
      >
        New Request
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          color: '#838996',
          fontSize: '0.78rem',
          mb: 1.75,
        }}
      >
        {investigationId
          ? 'Create a request in this folder — pick an agency, describe one aspect, then continue.'
          : 'Pick an agency, describe one aspect you want records about, then continue.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={submit}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr auto' }, gap: 1.25 }}
      >
        <TextField
          select
          size="small"
          label="Agency"
          value={destinationId}
          onChange={(e) => setDestinationId(e.target.value)}
          required
        >
          <MenuItem value="" disabled>
            Select agency…
          </MenuItem>
          {destinations.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Aspect"
          placeholder="e.g. salary and hiring decisions from 2022–2024"
          value={aspect}
          onChange={(e) => setAspect(e.target.value)}
          required
        />
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          startIcon={<Add fontSize="small" />}
          sx={{ textTransform: 'none', px: 2 }}
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </Box>
    </Paper>
  );
};

const InvestigationsStrip = ({ investigations, onOpen, onCreateClick }) => {
  const items = investigations || [];
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
        <Typography
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize: '0.95rem',
            color: '#00075e',
            flexGrow: 1,
          }}
        >
          Investigations
        </Typography>
        <Button
          size="small"
          onClick={onCreateClick}
          startIcon={<CreateNewFolder fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          New Investigation
        </Button>
      </Box>
      {items.length === 0 ? (
        <Typography sx={{ color: '#838996', fontSize: '0.82rem' }}>
          No folders yet — create one to group related requests and share them.
        </Typography>
      ) : (
      <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1.25 }}>
        {items.map((inv) => (
          <Paper
            key={inv.id}
            onClick={() => onOpen(inv)}
            elevation={0}
            sx={{
              border: '1px solid #e0ddd6',
              borderRadius: 2,
              px: 2,
              py: 1.25,
              cursor: 'pointer',
              minWidth: 200,
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: '#2a52be',
                boxShadow: '0 2px 8px rgba(42,82,190,0.1)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {inv.is_owner ? (
                <FolderOpen sx={{ color: '#2a52be', fontSize: 18 }} />
              ) : (
                <FolderShared sx={{ color: '#2a52be', fontSize: 18 }} />
              )}
              <Typography
                sx={{
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: '#00075e',
                  flexGrow: 1,
                }}
              >
                {inv.name}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontSize: '0.72rem',
                color: '#838996',
                mt: 0.25,
              }}
            >
              {inv.request_count} request{inv.request_count !== 1 ? 's' : ''}
              {inv.is_owner ? '' : ' · shared'}
            </Typography>
          </Paper>
        ))}
      </Stack>
      )}
    </Box>
  );
};

const RequestList = () => {
  const navigate = useNavigate();
  const { id: investigationId } = useParams(); // present when viewing /investigations/:id
  const folderMode = !!investigationId;
  const { user } = useAuth();

  const [requests, setRequests] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [viewMode, setViewMode] = useState('all');
  const [newInvDialog, setNewInvDialog] = useState(false);
  const [newInvName, setNewInvName] = useState('');
  const [newInvDesc, setNewInvDesc] = useState('');

  // Folder-mode state
  const [inv, setInv] = useState(null);
  const [shares, setShares] = useState([]);
  const [allRequests, setAllRequests] = useState([]);

  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editing, setEditing] = useState(false);

  const [shareDialog, setShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [shareError, setShareError] = useState('');

  const [attachDialog, setAttachDialog] = useState(false);
  const [attachId, setAttachId] = useState('');
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);

  const isOwner = inv && (inv.is_owner || inv.my_permission === 'owner');

  const load = async () => {
    setLoading(true);
    try {
      if (folderMode) {
        const [invR, reqR, shrR, destResp] = await Promise.all([
          investigationAPI.get(investigationId),
          praAPI.getRequests(null, false, investigationId),
          investigationAPI.listShares(investigationId),
          praAPI.getDestinations(),
        ]);
        setInv(invR.data);
        setEditName(invR.data.name);
        setEditDesc(invR.data.description || '');
        setRequests(reqR.data);
        setShares(shrR.data);
        setDestinations(destResp.data);
      } else {
        const [reqResp, destResp, invResp] = await Promise.all([
          praAPI.getRequests(null, viewMode === 'mine'),
          praAPI.getDestinations(),
          investigationAPI.list(),
        ]);
        setRequests(reqResp.data);
        setDestinations(destResp.data);
        setInvestigations(invResp.data);
      }
    } catch (error) {
      console.error('Error loading requests page:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderMode, investigationId, viewMode]);

  const handleDelete = async () => {
    try {
      await praAPI.deleteRequest(deleteDialog.id);
      setRequests(requests.filter((req) => req.id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null });
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const handleDetach = async (requestId) => {
    try {
      await investigationAPI.detachRequest(investigationId, requestId);
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (error) {
      console.error('Failed to remove request from folder:', error);
    }
  };

  const handleCreateInvestigation = async () => {
    if (!newInvName.trim()) return;
    try {
      await investigationAPI.create({ name: newInvName.trim(), description: newInvDesc.trim() || null });
      setNewInvName('');
      setNewInvDesc('');
      setNewInvDialog(false);
      load();
    } catch (err) {
      console.error('Failed to create investigation:', err);
    }
  };

  const handleSaveFolder = async () => {
    try {
      await investigationAPI.update(investigationId, { name: editName, description: editDesc });
      setInv((prev) => ({ ...prev, name: editName, description: editDesc }));
      setEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleDeleteFolder = async () => {
    try {
      await investigationAPI.delete(investigationId);
      setDeleteFolderDialog(false);
      navigate('/requests');
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const handleShare = async () => {
    setShareError('');
    try {
      await investigationAPI.share(investigationId, { email: shareEmail, permission: sharePermission });
      setShareEmail('');
      setSharePermission('view');
      const shrR = await investigationAPI.listShares(investigationId);
      setShares(shrR.data);
    } catch (e) {
      setShareError(e.response?.data?.detail || 'Share failed.');
    }
  };

  const handleUnshare = async (userId) => {
    try {
      await investigationAPI.unshare(investigationId, userId);
      setShares((prev) => prev.filter((s) => s.user_id !== userId));
    } catch (error) {
      console.error('Unshare failed:', error);
    }
  };

  const openAttach = async () => {
    try {
      const r = await praAPI.getRequests();
      setAllRequests(r.data.filter((req) => !requests.find((rr) => rr.id === req.id)));
      setAttachId('');
      setAttachDialog(true);
    } catch (error) {
      console.error('Failed to load requests.', error);
    }
  };

  const handleAttach = async () => {
    if (!attachId) return;
    try {
      await investigationAPI.attachRequest(investigationId, attachId);
      setAttachDialog(false);
      const reqR = await praAPI.getRequests(null, false, investigationId);
      setRequests(reqR.data);
    } catch (error) {
      console.error('Attach failed.', error);
    }
  };

  const getRequestTitle = (r) =>
    r.subject ||
    (r.natural_language_input
      ? r.natural_language_input.length > 80
        ? r.natural_language_input.substring(0, 80) + '…'
        : r.natural_language_input
      : `Request #${r.id}`);

  const getRelativeDays = (dateString) => {
    if (!dateString) return null;
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#838996' }}>Loading…</Typography>
        </Box>
      );
    }
    if (requests.length === 0) {
      return (
        <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#838996' }}>
            {folderMode
              ? 'No requests in this folder yet.'
              : viewMode === 'mine'
                ? "You haven't made any requests yet."
                : 'No requests yet.'}
          </Typography>
        </Box>
      );
    }
    return requests.map((request) => {
      const hasResponse = !!request.response_received_date || request.attached_email_count > 0;
      const isRequestOwner = request.user_id === user?.id;
      // In a folder, owners manage membership; otherwise the request owner can delete.
      const canManage = folderMode ? isOwner : isRequestOwner;
      return (
        <Card
          key={request.id}
          elevation={0}
          sx={{
            border: '1px solid #e0ddd6',
            borderRadius: 2,
            transition: 'all 0.15s ease',
            position: 'relative',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,7,94,0.10)', transform: 'translateY(-1px)' },
          }}
        >
          <CardActionArea onClick={() => navigate(`/requests/${request.id}`)} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, p: 2, pb: 1, alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '0.9rem', color: '#00075e', lineHeight: 1.4, mb: 0.25 }}>
                    {getRequestTitle(request)}
                  </Typography>
                  {!folderMode && viewMode === 'all' && request.owner_username && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Avatar sx={{ width: 16, height: 16, fontSize: '0.6rem', bgcolor: isRequestOwner ? '#2a52be' : '#838996' }}>
                        {request.owner_username[0].toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: '0.72rem', color: '#838996' }}>{request.owner_username}</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {hasResponse
                    ? <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    : <HourglassEmpty sx={{ color: '#c8c4bb', fontSize: 20 }} />}
                </Box>
              </Box>
              <Divider sx={{ borderColor: '#e0ddd6', mx: 2 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flexGrow: 1 }}>
                <Box sx={{ p: 2, borderRight: '1px solid #e0ddd6' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#838996', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    {request.destination || 'Recipient'}
                  </Typography>
                  <Chip label={statusLabels[request.status] || request.status} color={statusColors[request.status]} size="small" sx={{ mb: 0.75, height: 20, fontSize: '0.7rem' }} />
                  <Typography sx={{ fontSize: '0.72rem', color: '#c8c4bb' }}>
                    {request.sent_date ? `Sent ${getRelativeDays(request.sent_date)}` : 'Draft'}
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#838996', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    Response
                  </Typography>
                  {hasResponse ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <EmailIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#4caf50', fontWeight: 600 }}>Received</Typography>
                      </Box>
                      {request.attached_email_count > 0 && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#c8c4bb' }}>
                          {request.attached_email_count} email{request.attached_email_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography sx={{ fontSize: '0.8rem', color: '#c8c4bb', fontStyle: 'italic' }}>Awaiting…</Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </CardActionArea>
          {canManage && (
            <Tooltip title={folderMode ? 'Remove from folder' : 'Delete request'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (folderMode) handleDetach(request.id);
                  else setDeleteDialog({ open: true, id: request.id });
                }}
                sx={{ position: 'absolute', top: 6, right: 6, color: '#c8c4bb', '&:hover': { color: '#d32f2f' } }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Card>
      );
    });
  };

  const renderFolderHeader = () => {
    if (!inv) return null;
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton size="small" onClick={() => navigate('/requests')}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ color: '#838996' }}>Requests</Typography>
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #e0ddd6', borderRadius: 2, p: 2.5, mb: 3 }}>
          {editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Description"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSaveFolder} startIcon={<Save />}>Save</Button>
                <Button size="small" onClick={() => { setEditing(false); setEditName(inv.name); setEditDesc(inv.description || ''); }}>Cancel</Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderOpen sx={{ color: '#2a52be' }} />
                  <Typography variant="h5" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e' }}>
                    {inv.name}
                  </Typography>
                  {!inv.is_owner && (
                    <Chip size="small" label={`shared · ${inv.my_permission}`} sx={{ ml: 0.5 }} />
                  )}
                </Box>
                {inv.description && (
                  <Typography sx={{ color: '#838996', mt: 0.5, fontSize: '0.9rem' }}>{inv.description}</Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  <Chip size="small" label={`${requests.length} request${requests.length !== 1 ? 's' : ''}`} sx={{ mr: 0.75 }} />
                  <Chip size="small" label={`${shares.length} share${shares.length !== 1 ? 's' : ''}`} />
                </Box>
              </Box>
              {isOwner && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => setShareDialog(true)} startIcon={<PersonAdd />}>
                    Share
                  </Button>
                  <IconButton size="small" onClick={() => setEditing(true)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteFolderDialog(true)} sx={{ color: '#838996' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </>
    );
  };

  return (
    <Container maxWidth="lg">
      {folderMode ? (
        renderFolderHeader()
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
          <Box>
            <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e' }}>
              Requests
            </Typography>
            <Typography variant="body2" sx={{ color: '#838996', mt: 0.5 }}>
              {requests.length} request{requests.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="all"><Group sx={{ fontSize: 16, mr: 0.5 }} /> All</ToggleButton>
            <ToggleButton value="mine"><Person sx={{ fontSize: 16, mr: 0.5 }} /> Mine</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <QuickNewRequestCard destinations={destinations} investigationId={investigationId} onCreated={load} />

      {!folderMode && (
        <InvestigationsStrip
          investigations={investigations}
          onOpen={(invItem) => navigate(`/investigations/${invItem.id}`)}
          onCreateClick={() => setNewInvDialog(true)}
        />
      )}

      {folderMode && isOwner && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={openAttach} startIcon={<Description />}>
            Add Request
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {renderCards()}
      </Box>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Delete Request</DialogTitle>
        <DialogContent>Are you sure you want to delete this request? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteFolderDialog} onClose={() => setDeleteFolderDialog(false)}>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          Delete <strong>{inv?.name}</strong>? The requests inside are kept, just removed from this folder. This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteFolder} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newInvDialog} onClose={() => setNewInvDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Investigation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newInvName}
            onChange={(e) => setNewInvName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={newInvDesc}
            onChange={(e) => setNewInvDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewInvDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateInvestigation} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Share dialog (folder mode) */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Share Folder</DialogTitle>
        <DialogContent>
          {shareError && <Alert severity="error" sx={{ mb: 1.5 }}>{shareError}</Alert>}
          <TextField
            label="User email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Permission</InputLabel>
            <Select value={sharePermission} label="Permission" onChange={(e) => setSharePermission(e.target.value)}>
              <MenuItem value="view">View</MenuItem>
              <MenuItem value="edit">Edit</MenuItem>
            </Select>
          </FormControl>
          <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', fontSize: '0.85rem', mb: 1 }}>
            Currently shared with
          </Typography>
          {shares.length === 0 ? (
            <Typography sx={{ color: '#838996', fontSize: '0.85rem' }}>Not shared with anyone yet.</Typography>
          ) : (
            <List dense sx={{ border: '1px solid #e0ddd6', borderRadius: 1, p: 0 }}>
              {shares.map((s) => (
                <ListItem
                  key={s.user_id}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleUnshare(s.user_id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={s.username || s.email} secondary={s.permission} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog(false)}>Close</Button>
          <Button variant="contained" onClick={handleShare} disabled={!shareEmail.trim()}>Share</Button>
        </DialogActions>
      </Dialog>

      {/* Attach existing request (folder mode) */}
      <Dialog open={attachDialog} onClose={() => setAttachDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Request to Folder</DialogTitle>
        <DialogContent>
          {allRequests.length === 0 ? (
            <Typography sx={{ color: '#838996', fontSize: '0.9rem', mt: 1 }}>No other requests available to add.</Typography>
          ) : (
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>Request</InputLabel>
              <Select value={attachId} label="Request" onChange={(e) => setAttachId(e.target.value)}>
                {allRequests.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.subject || r.natural_language_input?.substring(0, 60) || `Request #${r.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAttach} disabled={!attachId}>Add</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RequestList;
